import { usePageTitle } from "@/hooks/usePageTitle";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { NavBar } from "@/components/NavBar";
import { Hero } from "@/components/Hero";
import { ProductGrid } from "@/components/ProductGrid";
import { fetchBeautyFashion, BEAUTY_CAT, FASHION_CAT } from "@/lib/lamka";
import { useLikeToggle } from "@/hooks/useLikeToggle";
import { useAuth } from "@/context/AuthContext";
import { useFollows } from "@/context/FollowsContext";
import { InstallPrompt, InstallLink } from "@/components/InstallPrompt";
import { rememberBrowse } from "@/lib/browse";
import { ThriftCloset } from "@/components/thrift/ThriftCloset";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Home() {
  usePageTitle();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const onToggleLike = useLikeToggle(setItems);

  useEffect(() => {
    if (authLoading) return;
    fetchBeautyFashion().then(setItems).catch(() => setError(true)).finally(() => setLoading(false));
  }, [authLoading, user?.user_id]);

  useEffect(() => {
    const nav = location.state?.nav;
    const q = location.state?.search;
    if ((nav || q) && !loading) {
      if (q) setSearch(q);
      setTimeout(() => (nav ? onNav(nav) : scrollTo("shop")), 50);
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, loading]);

  const { ids: followed } = useFollows();
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = followed.length
      ? [...items.filter((it) => followed.includes(it.seller_id)), ...items.filter((it) => !followed.includes(it.seller_id))]
      : items;
    return sorted.map((it) => ({ ...it, followed: followed.includes(it.seller_id) })).filter((it) => {
      if (filter === "beauty" && it.category !== BEAUTY_CAT) return false;
      if (filter === "fashion" && it.category !== FASHION_CAT) return false;
      if (!q) return true;
      return `${it.title} ${it.seller_name || ""} ${it.description || ""}`.toLowerCase().includes(q);
    });
  }, [items, filter, search, followed]);
  useEffect(() => rememberBrowse(visible), [visible]);

  const onNav = (action) => {
    if (action === "about") return scrollTo("about");
    setFilter(action);
    scrollTo("shop");
  };

  const onSearch = (v) => {
    setSearch(v);
    if (v) scrollTo("shop");
  };

  return (
    <div data-testid="home-page" className="bg-[#FBF9F5] min-h-screen">
      <NavBar onNav={onNav} search={search} onSearch={onSearch} />
      <Hero onExplore={() => scrollTo("shop")} />
      <ProductGrid items={visible} loading={loading} error={error} filter={filter} setFilter={setFilter} search={search} onToggleLike={onToggleLike} eyebrow={followed.length ? "Shops you follow first · then newest" : "Newest first"} />
      <ThriftCloset />
      <footer id="about" data-testid="about-section" className="bg-[#0F0F0F] text-white px-4 sm:px-8 lg:px-12 py-20 grid lg:grid-cols-[1fr_1.4fr] gap-12">
        <div>
          <p className="eyebrow mb-4">About</p>
          <p className="font-display text-3xl tracking-[0.15em] uppercase">Lamka <span className="text-[#C5A059]">Online</span></p>
        </div>
        <div className="text-white/60 text-sm sm:text-base leading-relaxed max-w-xl space-y-4">
          <p>Lamka Online is the marketplace where Lamka's sellers share their newest beauty and fashion finds — from skincare and cosmetics to everyday and occasion wear.</p>
          <InstallLink />
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">© {new Date().getFullYear()} Lamka Online</p>
        </div>
      </footer>
      <InstallPrompt />
    </div>
  );
}
